import React, { useEffect, useState } from "react";

export type SnackbarSeverity = "success" | "error" | "warning" | "info";
export type SnackbarPosition = "bottom-right" | "top-center";

interface SnackbarProps {
  message: string;
  severity?: SnackbarSeverity;
  open: boolean;
  onClose: () => void;
  autoHideDuration?: number;
  position?: SnackbarPosition;
}

const Snackbar: React.FC<SnackbarProps> = ({
  message,
  severity = "info",
  open,
  onClose,
  autoHideDuration = 6000,
  position = "bottom-right",
}) => {
  const [isVisible, setIsVisible] = useState(open);

  useEffect(() => {
    setIsVisible(open);
    if (open && autoHideDuration) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration, onClose]);

  if (!isVisible) return null;

  const getSeverityStyles = () => {
    switch (severity) {
      case "success":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "error":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "warning":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "info":
      default:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case "top-center":
        return "fixed top-4 left-1/2 transform -translate-x-1/2 z-50";
      case "bottom-right":
      default:
        return "fixed bottom-4 right-4 z-50";
    }
  };

  return (
    <div className={getPositionStyles()}>
      <div
        className={`${getSeverityStyles()} px-4 py-3 rounded-lg shadow-lg flex items-center justify-between min-w-[300px]`}
      >
        <div className="flex items-center">
          <span className="mr-2">
            {severity === "success" && "✓"}
            {/* {severity === "error" && "✕"} */}
            {severity === "warning" && "⚠"}
            {severity === "info" && "ℹ"}
          </span>
          <span>{message}</span>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            onClose();
          }}
          className="ml-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default Snackbar;
